// v2/config/forms.config.ts — Form field schemas as structured data

import { FormFieldSchema, StepDefinition } from './types';
import { INDIAN_STATES, ALL_INDIAN_STATES } from './locations.config';

// ─── Shared Validation Patterns ──────────────────────────────────────────────

/** Phone: 10 digits starting with 6-9 */
export const PHONE_PATTERN = { value: '^[6-9]\\d{9}$', message: 'Must be 10 digits starting with 6, 7, 8, or 9 (e.g. 9876543210)' };
/** PAN: 5 uppercase letters + 4 digits + 1 uppercase letter */
export const PAN_PATTERN = { value: '^[A-Z]{5}[0-9]{4}[A-Z]{1}$', message: 'Enter a valid PAN (e.g. ABCDE1234F)' };
/** IFSC: 4 uppercase letters + 0 + 6 alphanumeric */
export const IFSC_PATTERN = { value: '^[A-Z]{4}0[A-Z0-9]{6}$', message: 'Enter a valid IFSC (e.g. SBIN0001234)' };
/** Names: letters and spaces only */
export const NAME_PATTERN = { value: '^[a-zA-Z\\s]+$', message: 'Only letters and spaces are allowed' };
/** Single-word name: letters only, no spaces */
export const SINGLE_NAME_PATTERN = { value: '^[a-zA-Z]+$', message: 'Only letters are allowed (no spaces or numbers)' };
/** Postal code: exactly 6 digits */
export const POSTAL_CODE_PATTERN = { value: '^\\d{6}$', message: 'Must be exactly 6 digits (e.g. 500001)' };
/** Positive integer */
export const POSITIVE_INT_PATTERN = { value: '^[1-9]\\d*$', message: 'Enter a valid whole number' };
/** Amount with up to 2 decimal places */
export const AMOUNT_PATTERN = { value: '^[0-9]+(\\.[0-9]{1,2})?$', message: 'Enter a valid amount (up to 2 decimals, e.g. 15000.50)' };
/** Rate 0-100 with up to 2 decimal places */
export const RATE_PATTERN = { value: '^(?:100(?:\\.00?)?|\\d{1,2}(?:\\.\\d{1,2})?)$', message: 'Enter a valid rate 0–100 (e.g. 12.50)' };

// ─── Lead Creation Form ──────────────────────────────────────────────────────

export const LEAD_CREATION_FIELDS: FormFieldSchema[] = [
  {
    name: 'phoneNumber', label: 'Mobile No.', type: 'tel', placeholder: 'Enter mobile number',
    validation: { required: 'Mobile number is required', pattern: PHONE_PATTERN, maxLength: { value: 10, message: 'Must be exactly 10 digits' } },
    helperText: 'e.g. 9876543210', inputMode: 'numeric', numericOnly: true, order: 1,
  },
  {
    name: 'firstName', label: 'First Name', type: 'text', placeholder: 'Enter first name',
    validation: { required: 'First name is required', pattern: SINGLE_NAME_PATTERN, minLength: { value: 2, message: 'At least 2 characters' }, maxLength: { value: 50, message: 'At most 50 characters' } },
    order: 2,
  },
  {
    name: 'middleName', label: 'Middle Name', type: 'text', placeholder: 'Enter middle name',
    validation: { pattern: { value: '^[a-zA-Z]*$', message: 'Only letters allowed' }, maxLength: { value: 50, message: 'At most 50 characters' } },
    order: 3,
  },
  {
    name: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Enter last name',
    validation: { required: 'Last name is required', pattern: SINGLE_NAME_PATTERN, maxLength: { value: 50, message: 'At most 50 characters' } },
    order: 4,
  },
  {
    name: 'email', label: 'Email', type: 'email', placeholder: 'Enter email address',
    validation: { pattern: { value: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', message: 'Enter a valid email (e.g. user@example.com)' } },
    helperText: 'e.g. user@example.com', order: 5,
  },
  { name: 'state', label: 'State', type: 'select', options: INDIAN_STATES, validation: { required: 'State is required' }, order: 6 },
  { name: 'city', label: 'City', type: 'select', options: [], validation: { required: 'City is required' }, order: 7 },
  { name: 'addressLineOne', label: 'Address Line 1', type: 'text', placeholder: 'Enter address line 1', order: 8 },
  { name: 'addressLineTwo', label: 'Address Line 2', type: 'text', placeholder: 'Enter address line 2', order: 9 },
  {
    name: 'postalCode', label: 'Postal Code', type: 'text', placeholder: 'Enter postal code',
    validation: { pattern: POSTAL_CODE_PATTERN },
    helperText: 'e.g. 500001', inputMode: 'numeric', numericOnly: true, order: 10,
  },
  {
    name: 'loanTenure', label: 'Loan Tenure (Months)', type: 'tel', placeholder: 'Enter loan tenure in months',
    validation: { pattern: POSITIVE_INT_PATTERN, maxLength: { value: 3, message: 'At most 3 digits' } },
    helperText: 'e.g. 12', inputMode: 'numeric', numericOnly: true, order: 11,
  },
  {
    name: 'loanAmount', label: 'Loan Amount', type: 'tel', placeholder: 'Enter loan amount',
    validation: { pattern: AMOUNT_PATTERN, maxLength: { value: 10, message: 'Amount too large' } },
    helperText: 'e.g. 150000 or 150000.50', inputMode: 'decimal', decimalAllowed: true, order: 12,
  },
  {
    name: 'roi', label: 'ROI (%)', type: 'tel', placeholder: 'Enter rate of interest',
    validation: { pattern: RATE_PATTERN },
    helperText: 'e.g. 12.50', inputMode: 'decimal', decimalAllowed: true, order: 13,
  },
  {
    name: 'referrer', label: 'Referrer', type: 'text', placeholder: 'Enter referrer name',
    validation: { required: 'Referrer is required', pattern: NAME_PATTERN },
    order: 14,
  },
  {
    name: 'pilId', label: 'PIL Id', type: 'text', placeholder: 'Enter PIL Id',
    validation: { pattern: { value: '^[a-zA-Z0-9]{5,20}$', message: 'PIL Id must be 5–20 alphanumeric characters' } },
    order: 15,
  },
  {
    name: 'channel', label: 'Sourcing Channel', type: 'select',
    options: ['PIL BD', 'PIL Sales', 'PIL Contractor', 'Dealer', 'Website', 'Existing Customer', 'WhatsApp', 'FCC', 'Other', 'Call Center'],
    validation: { required: 'Sourcing channel is required' },
    order: 16,
  },
];

// ─── Loan Application Form — Section Fields ──────────────────────────────────

const KYC_DETAILS_FIELDS: FormFieldSchema[] = [
  {
    name: 'panNumber', label: 'PAN No.', type: 'text',
    validation: { required: true, pattern: PAN_PATTERN },
    helperText: 'e.g. ABCDE1234F', order: 1,
  },
  {
    name: 'aadharNumber', label: 'Aadhaar – last 4 digits', type: 'text',
    validation: { pattern: { value: '^\\d{4}$', message: 'Enter last 4 digits of Aadhaar (e.g. 1234)' } },
    helperText: 'e.g. 1234', inputMode: 'numeric', numericOnly: true, order: 2,
  },
  { name: 'voterIdNumber', label: 'Voter ID', type: 'text', helperText: 'e.g. ABC1234567', order: 3 },
  { name: 'passportNumber', label: 'Passport Number', type: 'text', helperText: 'e.g. H1506746', order: 4 },
  { name: 'drivingLicenseNumber', label: 'Driving License', type: 'text', helperText: 'e.g. DL0420160000001', order: 5 },
];

const PERSONAL_DETAILS_FIELDS: FormFieldSchema[] = [
  {
    name: 'firstName', label: 'First Name', type: 'text',
    validation: { required: true, pattern: SINGLE_NAME_PATTERN, minLength: { value: 2, message: 'At least 2 characters' }, maxLength: { value: 50, message: 'At most 50 characters' } },
    order: 1,
  },
  {
    name: 'middleName', label: 'Middle Name', type: 'text',
    validation: { pattern: { value: '^[a-zA-Z]*$', message: 'Only letters allowed' }, maxLength: { value: 50, message: 'At most 50 characters' } },
    order: 2,
  },
  {
    name: 'lastName', label: 'Last Name', type: 'text',
    validation: { required: true, pattern: SINGLE_NAME_PATTERN, maxLength: { value: 50, message: 'At most 50 characters' } },
    order: 3,
  },
  {
    name: 'fatherName', label: "Father's Name", type: 'text',
    validation: { pattern: NAME_PATTERN, maxLength: { value: 100, message: 'At most 100 characters' } },
    order: 4,
  },
  {
    name: 'motherName', label: "Mother's Name", type: 'text',
    validation: { pattern: NAME_PATTERN, maxLength: { value: 100, message: 'At most 100 characters' } },
    order: 5,
  },
  {
    name: 'spouseName', label: "Spouse's Name", type: 'text',
    validation: { pattern: NAME_PATTERN, maxLength: { value: 100, message: 'At most 100 characters' } },
    showIf: { field: 'personalDetails.maritalStatus', value: 'Married' },
    order: 6,
  },
  {
    name: 'phoneNumber', label: 'Mobile No.', type: 'tel',
    validation: { required: 'Mobile number is required', pattern: PHONE_PATTERN, maxLength: { value: 10, message: 'Must be exactly 10 digits' } },
    helperText: 'e.g. 9876543210', inputMode: 'numeric', numericOnly: true, order: 7,
  },
  {
    name: 'email', label: 'Email', type: 'email',
    validation: { pattern: { value: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$', message: 'Enter a valid email (e.g. user@example.com)' } },
    helperText: 'e.g. user@example.com', order: 8,
  },
  { name: 'maritalStatus', label: 'Marital Status', type: 'select', options: ['Unmarried', 'Married', 'Divorced', 'Widowed'], order: 9 },
];

const LOAN_REQUIREMENT_FIELDS: FormFieldSchema[] = [
  {
    name: 'amount', label: 'Loan Amount (₹)', type: 'tel',
    validation: { pattern: AMOUNT_PATTERN, maxLength: { value: 10, message: 'Amount too large' } },
    helperText: 'e.g. 150000 or 150000.50', inputMode: 'decimal', decimalAllowed: true, order: 1,
  },
  {
    name: 'rateOfInterest', label: 'Rate of Interest (%)', type: 'tel',
    validation: { pattern: RATE_PATTERN },
    helperText: 'e.g. 12.50', inputMode: 'decimal', decimalAllowed: true, order: 2,
  },
  {
    name: 'tenureInMonths', label: 'Loan Tenure (Months)', type: 'tel',
    validation: { pattern: POSITIVE_INT_PATTERN, maxLength: { value: 3, message: 'At most 3 digits' } },
    helperText: 'e.g. 24', inputMode: 'numeric', numericOnly: true, order: 3,
  },
];

const INCOME_DETAILS_FIELDS: FormFieldSchema[] = [
  {
    name: 'monthlyIncome', label: 'Monthly Income (₹)', type: 'tel',
    validation: { required: true, pattern: AMOUNT_PATTERN },
    helperText: 'e.g. 25000 or 25000.75', inputMode: 'decimal', decimalAllowed: true, order: 1,
  },
  {
    name: 'otherMonthlyIncome', label: 'Other Monthly Income (₹)', type: 'tel',
    validation: { pattern: AMOUNT_PATTERN },
    helperText: 'e.g. 5000', inputMode: 'decimal', decimalAllowed: true, order: 2,
  },
  { name: 'assets', label: 'Assets', type: 'multiselect', options: ['LAND', 'CAR', 'HOUSE', 'GOLD', 'SHARES', 'PLOT', 'FARM'], order: 3 },
];

const MEMBERSHIP_DETAILS_FIELDS: FormFieldSchema[] = [
  {
    name: 'memberName', label: 'PIL Member Name', type: 'text',
    validation: { pattern: NAME_PATTERN },
    order: 1,
  },
  {
    name: 'memberId', label: 'PIL Member ID', type: 'text',
    validation: { pattern: { value: '^[a-zA-Z0-9]{5,20}$', message: 'Must be 5–20 alphanumeric characters' } },
    order: 2,
  },
  { name: 'category', label: 'Category', type: 'text', order: 3 },
  { name: 'enrollmentDate', label: 'Enrollment Date', type: 'date', order: 4 },
  { name: 'clubName', label: 'Club Name', type: 'text', order: 5 },
  { name: 'clubId', label: 'Club ID', type: 'text', order: 6 },
  {
    name: 'bdName', label: 'BD Name', type: 'text',
    validation: { pattern: NAME_PATTERN },
    order: 7,
  },
  {
    name: 'bdPhoneNumber', label: 'BD Phone Number', type: 'tel',
    validation: { pattern: PHONE_PATTERN, maxLength: { value: 10, message: 'Must be exactly 10 digits' } },
    helperText: 'e.g. 9876543210', inputMode: 'numeric', numericOnly: true, order: 8,
  },
  { name: 'memberState', label: 'State', type: 'select', options: ALL_INDIAN_STATES, order: 9 },
  { name: 'memberCity', label: 'City', type: 'select', options: [], order: 10 },
  {
    name: 'pinCode', label: 'PIN Code', type: 'text',
    validation: { pattern: POSTAL_CODE_PATTERN },
    helperText: 'e.g. 500001', inputMode: 'numeric', numericOnly: true, order: 11,
  },
];

// ─── Loan Application Multi-Step Form — Combined Schema ──────────────────────
// NOTE: Application Details step removed — ID and productType are set during Phase 1 creation.

export const LOAN_APPLICATION_FIELDS: FormFieldSchema[] = [
  ...KYC_DETAILS_FIELDS.map((f) => ({ ...f, name: `kycDetails.${f.name}`, order: 100 + f.order })),
  ...PERSONAL_DETAILS_FIELDS.map((f) => ({ ...f, name: `personalDetails.${f.name}`, order: 200 + f.order })),
  ...LOAN_REQUIREMENT_FIELDS.map((f) => ({ ...f, name: `loanRequirementDetails.${f.name}`, order: 300 + f.order })),
  ...INCOME_DETAILS_FIELDS.map((f) => ({ ...f, name: `incomeDetails.${f.name}`, order: 400 + f.order })),
  ...MEMBERSHIP_DETAILS_FIELDS.map((f) => ({ ...f, name: `membershipDetails.${f.name}`, order: 900 + f.order })),
];

// ─── Loan Application Step Definitions ───────────────────────────────────────
// Steps are fully self-contained. Reorder by moving entries. Add new steps by appending.
// Custom component steps just need: isCustomComponent + customComponentName registered in the component registry.

export const LOAN_APPLICATION_STEPS: StepDefinition[] = [
  {
    name: 'personalDetails', label: 'Personal Details',
    description: 'Applicant name, contact, and marital status', icon: 'User',
    fields: PERSONAL_DETAILS_FIELDS.map((f) => `personalDetails.${f.name}`),
  },
  {
    name: 'kycDetails', label: 'KYC Details',
    description: 'PAN, Aadhaar, Voter ID, Passport, Driving License', icon: 'CheckCircle',
    fields: KYC_DETAILS_FIELDS.map((f) => `kycDetails.${f.name}`),
  },
  {
    name: 'loanRequirementDetails', label: 'Loan Requirements',
    description: 'Loan amount, interest rate, and tenure', icon: 'Wallet',
    fields: LOAN_REQUIREMENT_FIELDS.map((f) => `loanRequirementDetails.${f.name}`),
  },
  {
    name: 'incomeDetails', label: 'Income Details',
    description: 'Monthly income, other income, and assets', icon: 'Wallet',
    fields: INCOME_DETAILS_FIELDS.map((f) => `incomeDetails.${f.name}`),
  },
  {
    name: 'membershipDetails', label: 'PIL Member Details',
    description: 'Membership information', icon: 'BadgeCheck',
    fields: MEMBERSHIP_DETAILS_FIELDS.map((f) => `membershipDetails.${f.name}`),
  },
  {
    name: 'addressDetails', label: 'Addresses',
    description: 'Add one or more addresses', icon: 'MapPin',
    isCustomComponent: true, customComponentName: 'AddressManager',
  },
  {
    name: 'coApplicantDetails', label: 'Co-Applicant',
    description: 'Add co-applicant details', icon: 'Users',
    isCustomComponent: true, customComponentName: 'CoApplicantManager',
  },
  {
    name: 'referenceDetails', label: 'References',
    description: 'Add one or more references', icon: 'UserPlus',
    isCustomComponent: true, customComponentName: 'ReferenceManager',
  },
  {
    name: 'bankDetails', label: 'Bank Accounts',
    description: 'Add one or more bank accounts', icon: 'CreditCard',
    isCustomComponent: true, customComponentName: 'BankManager',
  },
  {
    name: 'documents', label: 'Documents',
    description: 'Upload required documents', icon: 'FileText',
    isCustomComponent: true, customComponentName: 'FileManager',
  },
  {
    name: 'review', label: 'Review',
    description: 'Review and submit your application', icon: 'CheckCircle',
    isCustomComponent: true, customComponentName: 'ReviewSection',
  },
];

// ─── Task Creation Form ──────────────────────────────────────────────────────

export const TASK_CREATION_FIELDS: FormFieldSchema[] = [
  { name: 'title', label: 'Title', type: 'text', placeholder: 'Enter task title', validation: { required: 'Title is required' }, order: 1 },
  { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Enter task description', validation: { required: 'Description is required' }, order: 2 },
  { name: 'assignee', label: 'Assignee', type: 'text', placeholder: 'Enter assignee', validation: { required: 'Assignee is required' }, order: 3 },
  { name: 'dueDate', label: 'Due Date', type: 'date', validation: { required: 'Due date is required' }, order: 4 },
  { name: 'priority', label: 'Priority', type: 'select', options: ['LOW', 'MEDIUM', 'HIGH'], validation: { required: 'Priority is required' }, order: 5 },
  {
    name: 'taskType', label: 'Task Type', type: 'select',
    options: [
      { label: 'Action', value: 'ACTION' }, { label: 'Approval', value: 'APPROVAL' },
      { label: 'Simple Task', value: 'SIMPLE_TASK' }, { label: 'Lead Follow Up', value: 'LEAD_FOLLOW_UP' },
      { label: 'Verification', value: 'VERIFICATION' }, { label: 'Pre-Approved Document Collection', value: 'PRE_APPROVED_DOCUMENT_COLLECTION' },
    ],
    validation: { required: 'Task type is required' },
    order: 6,
  },
];

// ─── Group Creation Form ─────────────────────────────────────────────────────

export const GROUP_CREATION_FIELDS: FormFieldSchema[] = [
  {
    name: 'name', label: 'Group Name', type: 'text', placeholder: 'Enter group name',
    validation: { required: 'Group name is required', pattern: NAME_PATTERN },
    order: 1,
  },
  { name: 'city', label: 'City', type: 'text', placeholder: 'Enter city', validation: { required: 'City is required' }, order: 2 },
  { name: 'coordinatorId', label: 'Coordinator', type: 'text', placeholder: 'Select coordinator', validation: { required: 'Coordinator is required' }, order: 3 },
  {
    name: 'bdManagerName', label: 'BD Manager Name', type: 'text', placeholder: 'Enter BD manager name',
    validation: { required: 'BD manager name is required', pattern: NAME_PATTERN },
    order: 4,
  },
  {
    name: 'bdManagerPhone', label: 'BD Manager Phone', type: 'tel', placeholder: 'Enter BD manager phone',
    validation: { required: 'BD manager phone is required', pattern: PHONE_PATTERN, maxLength: { value: 10, message: 'Must be exactly 10 digits' } },
    helperText: 'e.g. 9876543210', inputMode: 'numeric', numericOnly: true, order: 5,
  },
  { name: 'type', label: 'Group Type', type: 'text', placeholder: 'Enter group type (optional)', order: 6 },
  { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Enter description (optional)', order: 7 },
];

// ─── Residence Verification Form ─────────────────────────────────────────────

export const RESIDENCE_VERIFICATION_FIELDS: FormFieldSchema[] = [
  // ── Address Section ──
  { name: 'addressLineOne', label: 'Address Line 1', type: 'text', validation: { required: 'Address line 1 is required' }, order: 1 },
  { name: 'addressLineTwo', label: 'Address Line 2', type: 'text', order: 2 },
  { name: 'state', label: 'State', type: 'select', options: ALL_INDIAN_STATES, validation: { required: 'State is required' }, order: 3 },
  { name: 'city', label: 'City', type: 'select', options: [], validation: { required: 'City is required' }, order: 4 },
  {
    name: 'postalCode', label: 'Postal Code', type: 'text',
    validation: { required: 'Postal code is required', pattern: POSTAL_CODE_PATTERN },
    helperText: 'e.g. 500001', inputMode: 'numeric', numericOnly: true, order: 5,
  },
  { name: 'addressIsSameAsKYC', label: 'Address Same as KYC?', type: 'select', options: ['Yes', 'No'], order: 6 },
  { name: 'addressType', label: 'Address Type', type: 'select', options: ['PERMANENT', 'TEMPORARY'], order: 7 },
  { name: 'country', label: 'Country', type: 'text', placeholder: 'India', order: 8 },
  { name: 'ownership', label: 'Ownership', type: 'select', options: ['OWNED', 'RENTED', 'LEASED', 'COMPANY_PROVIDED', 'FAMILY_OWNED'], order: 9 },
  {
    name: 'monthlyRent', label: 'Monthly Rent (₹)', type: 'tel',
    validation: { pattern: AMOUNT_PATTERN },
    helperText: 'e.g. 5000', inputMode: 'decimal', decimalAllowed: true, order: 10,
  },
  {
    name: 'yearsOfStay', label: 'Years of Stay', type: 'tel',
    validation: { pattern: POSITIVE_INT_PATTERN },
    helperText: 'e.g. 3', inputMode: 'numeric', numericOnly: true, order: 11,
  },

  // ── Occupancy Section ──
  { name: 'occupancyStatus', label: 'Occupancy Status', type: 'select', options: ['Owned', 'Rented', 'Bachelor', 'Parental', 'Joint Family'], order: 20 },
  { name: 'applicantStayStatus', label: 'Applicant Stay Status', type: 'select', options: ['Permanent', 'Temporary', 'Does not stay'], order: 21 },

  // ── Household Section ──
  { name: 'noOfOccupant', label: 'No. of Occupants', type: 'select', options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'], order: 30 },
  { name: 'noOfEarningMember', label: 'No. of Earning Members', type: 'select', options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10'], order: 31 },
  { name: 'noOfMembersDependentOnApplicant', label: 'Members Dependent on Applicant', type: 'select', options: ['0', '1', '2', '3', '4', '5'], order: 32 },
  { name: 'noOfApplicantChildrenStudying', label: 'Children Studying', type: 'select', options: ['0', '1', '2', '3', '4'], order: 33 },

  // ── Property Section ──
  { name: 'noOfRooms', label: 'No. of Rooms', type: 'select', options: ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'], order: 40 },
  { name: 'houseWithKitchen', label: 'House with Kitchen?', type: 'select', options: ['Yes', 'No'], order: 41 },
  { name: 'houseAccessibility', label: 'House Accessibility', type: 'select', options: ['No road', '2W road', '4W road'], order: 42 },
  { name: 'gatedHouse', label: 'Gated House?', type: 'select', options: ['Yes', 'No'], order: 43 },
  { name: 'communityDominatedArea', label: 'Community Dominated Area?', type: 'select', options: ['Yes', 'No'], order: 44 },

  // ── Financial Section ──
  { name: 'electricityBillNo', label: 'Electricity Bill No.', type: 'text', order: 50 },
  {
    name: 'electricityBillAmount', label: 'Electricity Bill Amount (₹)', type: 'tel',
    validation: { pattern: AMOUNT_PATTERN },
    helperText: 'e.g. 1500', inputMode: 'decimal', decimalAllowed: true, order: 51,
  },
  {
    name: 'electricityBillOverdueAmount', label: 'Electricity Bill Overdue (₹)', type: 'tel',
    validation: { pattern: AMOUNT_PATTERN },
    inputMode: 'decimal', decimalAllowed: true, order: 52,
  },
  {
    name: 'monthlyResidentialExpenses', label: 'Monthly Residential Expenses (₹)', type: 'tel',
    validation: { pattern: AMOUNT_PATTERN },
    inputMode: 'decimal', decimalAllowed: true, order: 53,
  },
  {
    name: 'appliedLoanAmount', label: 'Applied Loan Amount (₹)', type: 'tel',
    validation: { pattern: AMOUNT_PATTERN },
    inputMode: 'decimal', decimalAllowed: true, order: 54,
  },

  // ── Spouse Section ──
  { name: 'spouseName', label: 'Spouse Name', type: 'text', validation: { pattern: NAME_PATTERN }, order: 60 },
  { name: 'spouseAwareOfLoan', label: 'Spouse Aware of Loan?', type: 'select', options: ['Yes', 'No'], order: 61 },

  // ── Verification Section ──
  { name: 'thirdPartyReference', label: 'Third Party Reference', type: 'select', options: ['Negative', 'Positive'], order: 70 },
  { name: 'standardOfLiving', label: 'Standard of Living', type: 'select', options: ['High', 'Medium', 'Low', 'Poor'], order: 71 },
  {
    name: 'verificationStatus', label: 'Verification Status', type: 'select',
    options: ['VERIFIED', 'NOT_VERIFIED', 'PARTIALLY_VERIFIED'],
    validation: { required: 'Verification status is required' },
    order: 72,
  },
  { name: 'remarks', label: 'Remarks', type: 'textarea', placeholder: 'Enter verification remarks', order: 73 },
  { name: 'agentRemarks', label: 'Agent Remarks', type: 'textarea', placeholder: 'Enter agent remarks', order: 74 },
];

// ─── Approved Loan Details (Soft Approval) ───────────────────────────────────

export const APPROVED_LOAN_FIELDS: FormFieldSchema[] = [
  {
    name: 'rateOfInterest', label: 'Rate of Interest (%)', type: 'tel',
    validation: { pattern: RATE_PATTERN },
    helperText: 'e.g. 12.50', inputMode: 'decimal', decimalAllowed: true, order: 1,
  },
  {
    name: 'amount', label: 'Loan Amount', type: 'tel',
    validation: { pattern: AMOUNT_PATTERN, maxLength: { value: 7, message: 'Amount must be at most 7 digits' } },
    helperText: 'e.g. 150000', inputMode: 'numeric', numericOnly: true, order: 2,
  },
  {
    name: 'tenureInMonths', label: 'Tenure (in months)', type: 'tel',
    validation: { pattern: POSITIVE_INT_PATTERN },
    helperText: 'e.g. 24', inputMode: 'numeric', numericOnly: true, order: 3,
  },
  {
    name: 'bureauScore', label: 'Bureau Score', type: 'tel',
    helperText: 'e.g. 750', inputMode: 'numeric', numericOnly: true, order: 4,
  },
];

// ─── Form Schema Registry ────────────────────────────────────────────────────

export const FORM_SCHEMAS: Record<string, FormFieldSchema[]> = {
  leadCreation: LEAD_CREATION_FIELDS,
  loanApplication: LOAN_APPLICATION_FIELDS,
  taskCreation: TASK_CREATION_FIELDS,
  groupCreation: GROUP_CREATION_FIELDS,
  residenceVerification: RESIDENCE_VERIFICATION_FIELDS,
  approvedLoanDetails: APPROVED_LOAN_FIELDS,
};

export const STEP_DEFINITIONS: Record<string, StepDefinition[]> = {
  loanApplication: LOAN_APPLICATION_STEPS,
};
